import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  errorToast,
  handleError,
  successToast,
} from "../../../../utils/common";
import useUserPermissions from "../../../../hooks/useUserPermissions";
import {
  Add,
  AttachFile,
  CheckCircle,
  Edit,
  LocationOnOutlined,
  Visibility,
} from "@mui/icons-material";
import KnowlarityCall from "../../../../components/Common/knowlarityCall";
import { LoadingButton } from "@mui/lab";
import useAxiosInstance from "../../../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../../../enums/common.enum";
import EditGstModal from "../../Modal/EditGstModal";
import EditBusinessPanModal from "../../Modal/EditBusinessPanModal";
import EditContactDetailsModal from "../../Modal/EditContactDetailsModal";
import BankLogo from "../../../Merchants/SingleMerchant/Summary/bankLogo";
import EditBankDetailsModal from "../../Modal/EditBankDetailsModal";
import EditCreditDetailsModal from "../../Modal/EditCreditDetailsModal";
import UploadAgreementModal from "../../Modal/UploadAgreementModal";
import EditBusinessDetailsModal from "../../Modal/EditBusinessDetailsModal";
import EditOwnerModal from "../../Modal/EditOwnerModal";
import UploadKycDocumentModal from "../../Modal/UploadKycDocumentModal";

const useStyles = makeStyles({
  accordion_collapsed: {
    borderRadius: "4px",
    "&::before": {
      backgroundColor: "transparent",
    },
  },
});

const KYC_DOCUMENTS = [
  "GST Certificate",
  "Business PAN",
  "Debt profile",
  "Bank statements for all bank accounts for the last 12 months",
  "GSTR-3B returns for the last 12 months",
  "Cancelled cheque",
  "Security Cheque",
];

const InfoRow = ({ label, value }: any) => (
  <Box display="flex" justifyContent="space-between" alignItems="center">
    <Typography color="secondary" variant="body2">
      {label}
    </Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Box>
);

const Summary = ({
  borrowerInfo,
  setBorrowerInfo,
  visitedTabs,
  updateVisitedTabs,
}: any) => {
  const [showBankDetailsModal, setShowBankDetailsModal] =
    useState<boolean>(false);
  const [showAllBanks, setShowAllBanks] = useState(false);
  const [showAllOwners, setShowAllOwners] = useState(false);
  const [bankModalType, setBankModalType] = useState<"add" | "edit">("edit");
  const [ownerModalType, setOwnerModalType] = useState<"add" | "edit">("edit");
  const [selectedBank, setSelectedBank] = useState<any>(null);
  const [selectedOwner, setSelectedOwner] = useState<any>(null);
  const [showOwnerModal, setShowOwnerModal] = useState<boolean>(false);
  const [showEditContactDetailsModal, setShowEditContactDetailsModal] =
    useState<boolean>(false);
  const [showEditGstModal, setShowEditGstModal] = useState<boolean>(false);
  const [showEditBusinessPanModal, setShowEditBusinessPanModal] =
    useState<boolean>(false);
  const [showEditCreditDetailsModal, setShowEditCreditDetailsModal] =
    useState<boolean>(false);
  const [showUploadAgreementModal, setShowUploadAgreementModal] =
    useState(false);
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [showEditBusinessDetailsModal, setShowEditBusinessDetailsModal] =
    useState<boolean>(false);
  const [showAllAgreements, setShowAllAgreements] = useState(false);
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const classes = useStyles();
  const userPermissions = useUserPermissions();
  const moduleScope = useMemo(
    () =>
      userPermissions?.find((item: any) => item.permission_id === "borrowers")
        ?.scope,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const privateInstance = useAxiosInstance();

  const handleStatus = async (newStatus: string) => {
    if (
      newStatus === "active" &&
      borrowerInfo?.kyc_docs?.status !== "completed"
    ) {
      alert("Please complete KYC before activating the borrower");
      return;
    }

    if (window.confirm("Are you sure?")) {
      let payload: any = {
        borrower_id: borrowerInfo?.borrower_id,
        status: newStatus,
      };

      if (newStatus === "blocked") {
        payload = {
          ...payload,
          block_reason: blockReason,
        };
      }

      try {
        setUpdatingStatus(true);
        const result = await privateInstance.post(
          `${API_VERSION.V3}/borrowers`,
          payload,
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          successToast("Status updated successfully");
          //update UI
          let updatedInfo = {
            ...borrowerInfo,
            status: newStatus,
          };
          if (newStatus === "blocked") {
            updatedInfo = {
              ...updatedInfo,
              block_reason: blockReason,
            };
          }
          setBorrowerInfo(updatedInfo);
          //end
        } else {
          errorToast(result.data.message);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setUpdatingStatus(false);
      }
    }
  };

  const formatDateTime = (date?: string) => {
    if (!date) return "—";

    const d = new Date(date);

    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatLabel = (value?: string) => {
    if (!value) return "—";

    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getFileNameFromUrl = (url?: string) => {
    if (!url) return "Agreement";
    try {
      return decodeURIComponent(url.split("/").pop() || "Agreement");
    } catch {
      return url.split("/").pop() || "Agreement";
    }
  };

  const formatAadhaar = (aadhaar?: string) => {
    if (!aadhaar) return "—";
    return aadhaar
      .replace(/\D/g, "")
      .replace(/(\d{4})(\d{4})(\d{4})/, "$1 $2 $3");
  };

  const banks = borrowerInfo?.bank_details || [];
  const visibleBanks = showAllBanks ? banks : banks.slice(0, 2);
  const hiddenCount = banks.length - 2;

  const owners = borrowerInfo?.owner_details || [];
  const visibleOwners = showAllOwners ? owners : owners.slice(0, 2);
  const hiddenOwnersCount = owners.length - 2;

  const handleDeleteKycDocument = async (docToDelete: any) => {
    if (!window.confirm("Are you sure you want to remove this document?")) {
      return;
    }

    try {
      const remainingDocs = borrowerInfo.kyc_docs.docs_url.filter(
        (doc: any) =>
          (typeof doc === "string" ? doc : doc.url) !==
          (typeof docToDelete === "string" ? docToDelete : docToDelete.url),
      );

      const status =
        remainingDocs.length === KYC_DOCUMENTS.length ? "completed" : "pending";

      const payload = {
        borrower_id: borrowerInfo.borrower_id,
        kyc_docs: {
          ...borrowerInfo.kyc_docs,
          status,
          docs_url: remainingDocs,
        },
      };

      const res = await privateInstance.post(
        `${API_VERSION.V3}/borrowers`,
        payload,
      );

      if (res?.data?.status === API_RESULT.SUCCESS) {
        setBorrowerInfo((prev: any) => ({
          ...prev,
          kyc_docs: payload.kyc_docs,
        }));

        successToast("Document removed successfully");
      } else {
        errorToast(res.data.message);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const canEdit = moduleScope?.includes("edit");
  const hasNoBank =
    !borrowerInfo?.bank_details || borrowerInfo.bank_details.length === 0;

  const isBorrowerProfileComplete = useMemo(() => {
    if (!borrowerInfo) return false;

    const hasContactDetails =
      borrowerInfo.contact_details?.contact_no1 &&
      borrowerInfo.contact_details?.address &&
      borrowerInfo.contact_details?.city &&
      borrowerInfo.contact_details?.state &&
      borrowerInfo.contact_details?.pincode;

    const hasBusinessDetails =
      borrowerInfo.business_pan_no &&
      borrowerInfo.gst_no &&
      borrowerInfo.business_details?.business_type;

    const hasOwnerDetails =
      Array.isArray(borrowerInfo.owner_details) &&
      borrowerInfo.owner_details.length > 0;

    const hasBankDetails =
      Array.isArray(borrowerInfo.bank_details) &&
      borrowerInfo.bank_details.length > 0;

    const hasCreditDetails =
      borrowerInfo.credit_details?.credit_limit &&
      borrowerInfo.credit_details?.nbfc_partner;

    return (
      hasContactDetails &&
      hasBusinessDetails &&
      hasOwnerDetails &&
      hasBankDetails &&
      hasCreditDetails
    );
  }, [borrowerInfo]);

  const renderDisbursementAccount = () => {
    const type = borrowerInfo?.credit_details?.disbursement_type;

    if (type !== "self") {
      return formatLabel(type);
    }

    const bank = borrowerInfo?.bank_details?.[0];

    if (!bank) return "—";

    const accountNumber = bank.account_no || "";
    const last4 = accountNumber.slice(-4);

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <BankLogo name={bank.bank} />
        <Typography variant="body2">
          {bank.bank} • XX{last4}
        </Typography>
      </Stack>
    );
  };

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ border: "1px solid #D6D9DE", borderRadius: 1.5, p: 1.5 }}
          >
            <Typography color="secondary" variant="body2">
              Phone No.
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Typography variant="body2">
                +91 {borrowerInfo?.contact_details?.contact_no1 || "--"}
              </Typography>
              {canEdit && (
                <KnowlarityCall
                  order={{
                    order_id: borrowerInfo?.borrower_id as string,
                    mobile: borrowerInfo?.contact_details?.contact_no1,
                  }}
                  showNumber={false}
                  renderSmallIcon={true}
                  service="borrower"
                />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ border: "1px solid #D6D9DE", borderRadius: 1.5, p: 1.5 }}
          >
            <Typography color="secondary" variant="body2">
              GST Name / No.
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">
                {borrowerInfo?.name || "--"} / {borrowerInfo?.gst_no || "--"}
              </Typography>
              {moduleScope?.includes("edit") ? (
                <Edit
                  onClick={() => setShowEditGstModal(true)}
                  color="primary"
                  sx={{ cursor: "pointer" }}
                  fontSize="small"
                />
              ) : null}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ border: "1px solid #D6D9DE", borderRadius: 1.5, p: 1.5 }}
          >
            <Typography color="secondary" variant="body2">
              Business PAN
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">
                {borrowerInfo?.business_pan_no || "--"}
              </Typography>
              {moduleScope?.includes("edit") ? (
                <Edit
                  onClick={() => setShowEditBusinessPanModal(true)}
                  color="primary"
                  sx={{ cursor: "pointer" }}
                  fontSize="small"
                />
              ) : null}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={0}>
        <Grid item xs={12} md={6}>
          <Stack gap={2}>
            <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "#F0F1F5",
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                  p: 1.5,
                  z: 1,
                  position: "relative",
                }}
              >
                <Typography fontWeight="bold" variant="body2">
                  Business Details
                </Typography>
                {moduleScope?.includes("edit") ? (
                  <Edit
                    onClick={() => setShowEditBusinessDetailsModal(true)}
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    fontSize="small"
                  />
                ) : null}
              </Box>
              <Box p={1.5}>
                <Stack>
                  <Typography color="secondary">Business Type</Typography>
                  <Typography variant="body2">
                    {formatLabel(
                      borrowerInfo?.business_details?.business_type,
                    ) || "--"}
                  </Typography>
                </Stack>

                <Box py={1}>
                  <Divider />
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Stack>
                    <Typography color="secondary">CIN Number</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.business_details?.cin_no || "--"}
                    </Typography>
                  </Stack>
                  <Stack alignItems="end">
                    <Typography color="secondary">TIN Number</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.business_details?.tin_no || "--"}
                    </Typography>
                  </Stack>
                </Box>

                <Box py={1}>
                  <Divider />
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Stack>
                    <Typography color="secondary">UIN Number</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.business_details?.uin_no || "--"}
                    </Typography>
                  </Stack>
                  <Stack alignItems="end">
                    <Typography color="secondary">CST Number</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.business_details?.cst_no || "--"}
                    </Typography>
                  </Stack>
                </Box>

                <Box py={1}>
                  <Divider />
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Stack>
                    <Typography color="secondary">DL Number</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.business_details?.dl_no || "--"}
                    </Typography>
                  </Stack>
                  <Stack alignItems="end">
                    <Typography color="secondary">FSSAI Number</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.business_details?.fssai_no || "--"}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Box>

            <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "#F0F1F5",
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                  p: 1.5,
                  z: 1,
                  position: "relative",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Bank Details
                </Typography>
                {canEdit && hasNoBank && (
                  <Add
                    onClick={() => {
                      setSelectedBank(null);
                      setBankModalType("add");
                      setShowBankDetailsModal(true);
                    }}
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    fontSize="small"
                  />
                )}
              </Box>

              <Stack gap={1.5} p={1.5}>
                {banks.length ? (
                  <>
                    {visibleBanks.map((bank: any, index: number) => (
                      <React.Fragment key={bank.account_no}>
                        {/* Bank Header */}
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <BankLogo name={bank.bank} />

                            <Typography variant="body2" fontWeight={600}>
                              {bank.bank}
                            </Typography>

                            {bank.is_primary && (
                              <Tooltip title="Primary Account">
                                <CheckCircle
                                  fontSize="small"
                                  sx={{ color: "#4CAF50" }}
                                />
                              </Tooltip>
                            )}
                          </Box>

                          {moduleScope?.includes("edit") && (
                            <Edit
                              fontSize="small"
                              color="primary"
                              sx={{ cursor: "pointer" }}
                              onClick={() => {
                                setSelectedBank(bank);
                                setBankModalType("edit");
                                setShowBankDetailsModal(true);
                              }}
                            />
                          )}
                        </Box>

                        <Stack gap={0.75} mt={0.5}>
                          <InfoRow
                            label="Account Holder Name"
                            value={bank.account_name}
                          />
                          <InfoRow
                            label="Account Number"
                            value={bank.account_no}
                          />
                          <InfoRow label="IFSC Code" value={bank.ifsc_code} />
                          <InfoRow
                            label="Account Type"
                            value={
                              bank.account_type
                                ? bank.account_type.charAt(0).toUpperCase() +
                                  bank.account_type.slice(1).toLowerCase()
                                : "—"
                            }
                          />
                          <InfoRow label="Branch" value={bank.branch} />
                        </Stack>

                        {/* Divider */}
                        {index < visibleBanks.length - 1 && (
                          <Divider sx={{ my: 1.5 }} />
                        )}
                      </React.Fragment>
                    ))}

                    {banks.length > 2 && (
                      <Box display="flex" justifyContent="center" mt={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="primary"
                          onClick={() => setShowAllBanks((prev) => !prev)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 500,
                            px: 2.5,
                          }}
                        >
                          {showAllBanks
                            ? "View less"
                            : `View ${hiddenCount} more bank${hiddenCount > 1 ? "s" : ""}`}
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="secondary">
                    No Bank Details Found.
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "#F0F1F5",
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                  p: 1.5,
                  z: 1,
                  position: "relative",
                }}
              >
                <Typography fontWeight="bold" variant="body2">
                  KYC Documents
                </Typography>
              </Box>

              <Box p={1.5}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography color="secondary">KYC Status</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.kyc_docs?.status ? (
                        borrowerInfo.kyc_docs.status === "completed" ? (
                          <Chip
                            size="small"
                            label="Completed"
                            sx={{
                              backgroundColor: "#C6F6D5",
                              color: "#22543D",
                              fontWeight: 500,
                              px: 1,
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="Pending"
                            sx={{
                              backgroundColor: "#FEEBC8",
                              color: "#7B341E",
                              fontWeight: 500,
                              px: 1,
                            }}
                          />
                        )
                      ) : (
                        "—"
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6} textAlign="right">
                    <Typography color="secondary">
                      Date of Submission
                    </Typography>

                    <Typography variant="body2">
                      {borrowerInfo?.kyc_docs?.date_of_submission
                        ? new Date(
                            borrowerInfo.kyc_docs.date_of_submission,
                          ).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "—"}
                    </Typography>
                  </Grid>
                </Grid>

                <Box py={2}>
                  <Divider />
                </Box>

                <Stack gap={1.5}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography fontWeight={600} variant="body2">
                      Documents
                    </Typography>

                    {moduleScope?.includes("edit") &&
                      borrowerInfo?.kyc_docs?.status !== "completed" && (
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          onClick={() => setShowUploadDocumentModal(true)}
                        >
                          + Add Document
                        </LoadingButton>
                      )}
                  </Box>

                  {borrowerInfo?.kyc_docs?.docs_url?.length ? (
                    <>
                      {(showAllDocuments
                        ? borrowerInfo.kyc_docs.docs_url
                        : borrowerInfo.kyc_docs.docs_url.slice(0, 2)
                      ).map((doc: any, index: number) => {
                        const url = typeof doc === "string" ? doc : doc.url;
                        const fileName =
                          typeof doc === "string"
                            ? getFileNameFromUrl(doc)
                            : doc.name;

                        return (
                          <Box
                            key={url || index}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <CheckCircle
                                fontSize="small"
                                sx={{ color: "#4CAF50" }}
                              />

                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 240,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={fileName}
                              >
                                {formatLabel(fileName)}
                              </Typography>
                            </Box>

                            <Box display="flex" alignItems="center" gap={0.5}>
                              {/* View */}
                              <Tooltip title="View Document">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(url, "_blank")}
                                >
                                  <Visibility
                                    fontSize="small"
                                    color="primary"
                                  />
                                </IconButton>
                              </Tooltip>

                              {/* Delete – hide when KYC completed */}
                              {moduleScope?.includes("edit") && (
                                <Tooltip title="Remove Document">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteKycDocument(doc)}
                                  >
                                    ✕
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        );
                      })}

                      {borrowerInfo.kyc_docs.docs_url.length > 2 && (
                        <Box display="flex" justifyContent="center">
                          <LoadingButton
                            size="small"
                            variant="outlined"
                            onClick={() => setShowAllDocuments((prev) => !prev)}
                            sx={{ mt: 0.5 }}
                          >
                            {showAllDocuments
                              ? "View less"
                              : `View ${
                                  borrowerInfo.kyc_docs.docs_url.length - 2
                                } more document${
                                  borrowerInfo.kyc_docs.docs_url.length - 2 > 1
                                    ? "s"
                                    : ""
                                }`}
                          </LoadingButton>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="secondary">
                      No Document Uploaded
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>

            {["active", "inactive"].includes(borrowerInfo?.status) &&
            canEdit ? (
              <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
                <Box
                  sx={{
                    backgroundColor: "#F0F1F5",
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    p: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "capitalize" }}
                  >
                    <strong>Status: {borrowerInfo?.status}</strong>
                  </Typography>
                </Box>
                <Stack gap={1} p={1.5}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1.5}
                  >
                    {borrowerInfo?.status === "active" ? (
                      <>
                        <TextField
                          multiline
                          rows={2}
                          fullWidth
                          id="reason"
                          size="small"
                          label="Block Reason"
                          value={blockReason}
                          onChange={(event) => {
                            setBlockReason(event.target.value);
                          }}
                          disabled={updatingStatus}
                        />
                        <LoadingButton
                          size="small"
                          color="error"
                          loading={updatingStatus}
                          loadingIndicator={
                            <CircularProgress
                              size={20}
                              sx={{ color: "#fff" }}
                            />
                          }
                          variant="contained"
                          type="button"
                          onClick={() => handleStatus("blocked")}
                          disabled={!blockReason?.trim()}
                        >
                          Block
                        </LoadingButton>
                      </>
                    ) : (
                      <>
                        {["inactive", "blocked"].includes(
                          borrowerInfo?.status,
                        ) && (
                          <Stack gap={0.75}>
                            {borrowerInfo?.block_reason && (
                              <Typography
                                sx={{ textTransform: "capitalize" }}
                                variant="body2"
                                color="secondary"
                              >
                                {borrowerInfo.block_reason}
                              </Typography>
                            )}

                            {!isBorrowerProfileComplete && (
                              <Typography variant="body2" color="error">
                                Please complete all mandatory details to
                                activate the borrower.
                              </Typography>
                            )}
                          </Stack>
                        )}
                        <LoadingButton
                          sx={{ px: 3 }}
                          size="small"
                          color="success"
                          loading={updatingStatus}
                          loadingIndicator={
                            <CircularProgress
                              size={20}
                              sx={{ color: "#fff" }}
                            />
                          }
                          disabled={!isBorrowerProfileComplete}
                          variant="contained"
                          type="button"
                          onClick={() => handleStatus("active")}
                        >
                          Activate
                        </LoadingButton>
                      </>
                    )}
                  </Box>
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack gap={2}>
            {/* <KycDetailsView
              activeLocationId={providerInfo?.location_id}
              kycData={kycData}
              setKycData={setKycData}
              visitedTabs={visitedTabs}
              updateVisitedTabs={updateVisitedTabs}
            /> */}

            <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
              {/* Header */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "#F0F1F5",
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                  p: 1.5,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Owner Details
                </Typography>

                {moduleScope?.includes("edit") && (
                  <Add
                    fontSize="small"
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedOwner(null);
                      setOwnerModalType("add");
                      setShowOwnerModal(true);
                    }}
                  />
                )}
              </Box>

              <Stack gap={2} p={1.5}>
                {owners.length ? (
                  <>
                    {(showAllOwners ? owners : owners.slice(0, 2)).map(
                      (owner: any, index: number) => (
                        <React.Fragment key={owner.owner_id || index}>
                          {/* Top Row */}
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box display="flex" gap={1.5}>
                              {/* Owner Image */}
                              <Avatar
                                src={owner.owner_image_url}
                                sx={{ width: 48, height: 48 }}
                              />

                              {/* Name & Phone */}
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {owner.owner_name || "—"}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {owner.phone_number || "—"}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Edit */}
                            {moduleScope?.includes("edit") && (
                              <Edit
                                fontSize="small"
                                color="primary"
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                  setSelectedOwner(owner);
                                  setOwnerModalType("edit");
                                  setShowOwnerModal(true);
                                }}
                              />
                            )}
                          </Box>

                          {/* Aadhaar & PAN */}
                          <Stack direction="row" spacing={2} mt={1}>
                            {/* Aadhaar */}
                            <Box
                              flex={1}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                backgroundColor: "#F8F9FC",
                                borderRadius: 1,
                                p: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Aadhaar Card
                                </Typography>
                                <Typography variant="body2">
                                  {formatAadhaar(owner.aadhaar_no)}
                                </Typography>
                              </Box>

                              {owner.aadhaar_url && (
                                <Tooltip title="View Aadhaar">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      window.open(owner.aadhaar_url, "_blank")
                                    }
                                  >
                                    <AttachFile
                                      fontSize="small"
                                      color="primary"
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>

                            {/* PAN */}
                            <Box
                              flex={1}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              sx={{
                                backgroundColor: "#F8F9FC",
                                borderRadius: 1,
                                p: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  PAN Card
                                </Typography>
                                <Typography variant="body2">
                                  {owner.pan_no || "—"}
                                </Typography>
                              </Box>

                              {owner.pan_url && (
                                <Tooltip title="View PAN">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      window.open(owner.pan_url, "_blank")
                                    }
                                  >
                                    <AttachFile
                                      fontSize="small"
                                      color="primary"
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Stack>

                          {/* Divider */}
                          {index <
                            (showAllOwners
                              ? owners.length
                              : Math.min(2, owners.length)) -
                              1 && <Divider />}
                        </React.Fragment>
                      ),
                    )}

                    {/* View More / Less */}
                    {owners.length > 2 && (
                      <Box display="flex" justifyContent="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowAllOwners((prev) => !prev)}
                          sx={{ textTransform: "none", fontWeight: 500 }}
                        >
                          {showAllOwners
                            ? "View less"
                            : `View ${owners.length - 2} more owner${
                                owners.length - 2 > 1 ? "s" : ""
                              }`}
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="secondary">
                    No Owner Details Found.
                  </Typography>
                )}
              </Stack>
            </Box>

            <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "#F0F1F5",
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                  p: 1.5,
                  z: 1,
                  position: "relative",
                }}
              >
                <Typography fontWeight="bold" variant="body2">
                  Credit Details
                </Typography>
                {moduleScope?.includes("edit") ? (
                  <Edit
                    onClick={() => setShowEditCreditDetailsModal(true)}
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    fontSize="small"
                  />
                ) : null}
              </Box>

              <Box p={1.5}>
                <Grid container spacing={2} alignItems="center">
                  {/* LEFT COLUMN – CREDIT INFO */}
                  <Grid item xs={12} md={8}>
                    <Stack gap={1}>
                      <InfoRow
                        label="NBFC Partner"
                        value={formatLabel(
                          borrowerInfo?.credit_details?.nbfc_partner,
                        )}
                      />
                      <InfoRow
                        label="Repayment Tenure (Days)"
                        value={
                          borrowerInfo?.credit_details?.emi_repayment_mode
                            ? borrowerInfo?.credit_details?.emi_repayment_mode
                                .charAt(0)
                                .toUpperCase() +
                              borrowerInfo?.credit_details?.emi_repayment_mode
                                .slice(1)
                                .toLowerCase()
                            : "—"
                        }
                      />
                      <InfoRow
                        label="Interest Rate %"
                        value={
                          borrowerInfo?.credit_details?.interest_rate != null
                            ? `${borrowerInfo.credit_details.interest_rate}%`
                            : "--"
                        }
                      />

                      <InfoRow
                        label="Processing Fees %"
                        value={
                          borrowerInfo?.credit_details?.processing_fee != null
                            ? `${borrowerInfo.credit_details.processing_fee}%`
                            : "--"
                        }
                      />

                      <InfoRow
                        label="Late Fees (/day) %"
                        value={
                          borrowerInfo?.credit_details?.late_fee != null
                            ? `${borrowerInfo.credit_details.late_fee}%`
                            : "--"
                        }
                      />

                      <InfoRow
                        label="Disbursement Account"
                        value={renderDisbursementAccount()}
                      />
                    </Stack>
                  </Grid>

                  {/* RIGHT COLUMN – CIRCULAR PROGRESS */}
                  <Grid item xs={12} md={4}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Box position="relative" display="inline-flex">
                        {/* Background Circle */}
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={120}
                          thickness={4}
                          sx={{ color: "#E6EEF6" }}
                        />

                        {/* Progress Circle */}
                        <CircularProgress
                          variant="determinate"
                          value={
                            borrowerInfo?.credit_details?.credit_limit
                              ? (borrowerInfo.credit_details?.used_limit /
                                  borrowerInfo.credit_details?.credit_limit) *
                                100
                              : 0
                          }
                          size={120}
                          thickness={4}
                          sx={{
                            color: "#1976D2",
                            position: "absolute",
                            left: 0,
                          }}
                        />

                        {/* Center Text */}
                        <Box
                          top={0}
                          left={0}
                          bottom={0}
                          right={0}
                          position="absolute"
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Typography variant="h6" fontWeight={600}>
                            {borrowerInfo?.credit_details?.credit_limit
                              ? Math.round(
                                  (borrowerInfo.credit_details?.used_limit /
                                    borrowerInfo.credit_details?.credit_limit) *
                                    100,
                                )
                              : 0}
                            %
                          </Typography>
                          <Typography variant="body2" color="secondary">
                            ₹{borrowerInfo?.credit_details?.used_limit || 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    borderRadius: 1.5,
                    border: "1px solid #9EE3E1",
                    background: "#ecfffe",
                  }}
                  p={1.5}
                  mt={1.5}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="body2" color="secondary">
                        Credit Limit
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ₹{borrowerInfo?.credit_details?.credit_limit || 0}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="secondary">
                        Used Limit
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ₹{borrowerInfo?.credit_details?.used_limit || 0}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="secondary">
                        Available Limit
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ₹{borrowerInfo?.credit_details?.available_limit || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box py={2}>
                  <Divider />
                </Box>

                <Stack gap={1.5}>
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography fontWeight={600} variant="body2">
                      Signed Agreements
                    </Typography>

                    {moduleScope?.includes("edit") && (
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        onClick={() => setShowUploadAgreementModal(true)}
                      >
                        + Add Agreement
                      </LoadingButton>
                    )}
                  </Box>

                  {/* Agreements List */}
                  {borrowerInfo?.credit_details?.agreement_url?.length ? (
                    <>
                      {(showAllAgreements
                        ? borrowerInfo.credit_details.agreement_url
                        : borrowerInfo.credit_details.agreement_url.slice(0, 2)
                      ).map((doc: any, index: number) => {
                        const url = typeof doc === "string" ? doc : doc.url;
                        const fileName =
                          typeof doc === "string"
                            ? getFileNameFromUrl(doc) // backward compatibility
                            : doc.name;

                        return (
                          <Box
                            key={url || index}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box display="flex" alignItems="center" gap={1}>
                              <CheckCircle
                                fontSize="small"
                                sx={{ color: "#4CAF50" }}
                              />

                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 240,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={fileName}
                              >
                                {formatLabel(fileName)}
                              </Typography>
                            </Box>

                            <Tooltip title="View Agreement">
                              <IconButton
                                size="small"
                                onClick={() => window.open(url, "_blank")}
                              >
                                <Visibility fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })}

                      {/* View More / Less */}
                      {borrowerInfo.credit_details.agreement_url.length > 2 && (
                        <Box display="flex" justifyContent="center">
                          <LoadingButton
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setShowAllAgreements((prev) => !prev)
                            }
                            sx={{ mt: 0.5 }}
                          >
                            {showAllAgreements
                              ? "View less"
                              : `View ${
                                  borrowerInfo.credit_details.agreement_url
                                    .length - 2
                                } more agreement${
                                  borrowerInfo.credit_details.agreement_url
                                    .length -
                                    2 >
                                  1
                                    ? "s"
                                    : ""
                                }`}
                          </LoadingButton>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="secondary">
                      No Agreement Uploaded
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "#F0F1F5",
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                  p: 1.5,
                  z: 1,
                  position: "relative",
                }}
              >
                <Typography fontWeight="bold" variant="body2">
                  Contact Details
                </Typography>
                {moduleScope?.includes("edit") ? (
                  <Edit
                    onClick={() => setShowEditContactDetailsModal(true)}
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    fontSize="small"
                  />
                ) : null}
              </Box>
              <Box p={1.5}>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <LocationOnOutlined color="primary" fontSize="small" />
                  <Stack>
                    <Typography color="secondary">Address:</Typography>

                    <Typography variant="body2">
                      {borrowerInfo?.contact_details?.address}
                    </Typography>
                  </Stack>
                </Box>
                <Grid mt={0} container spacing={2}>
                  <Grid item xs={6} md={4} textAlign="left">
                    <Typography color="secondary">City</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.contact_details?.city || "--"}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    md={4}
                    display="flex"
                    justifyContent="center"
                  >
                    <Stack alignItems="flex-start">
                      <Typography color="secondary">State</Typography>
                      <Typography variant="body2">
                        {borrowerInfo?.contact_details?.state || "--"}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6} md={4} textAlign="right">
                    <Typography color="secondary">Pincode</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.contact_details?.pincode || "--"}
                    </Typography>
                  </Grid>
                </Grid>
                <Box py={2}>
                  <Divider />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Stack>
                    <Typography color="secondary">Contact Numbers</Typography>
                    <Typography variant="body2">
                      {borrowerInfo?.contact_details?.contact_no2
                        ? `${borrowerInfo?.contact_details?.contact_no1}, ${borrowerInfo?.contact_details?.contact_no2}`
                        : borrowerInfo?.contact_details?.contact_no1}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography color="secondary">Email ID</Typography>
                    {borrowerInfo?.contact_details?.email_ids?.length ? (
                      borrowerInfo.contact_details.email_ids.map(
                        (email: string, index: number) => (
                          <Typography key={index} variant="body2">
                            {email}
                          </Typography>
                        ),
                      )
                    ) : (
                      <Typography variant="body2">—</Typography>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>

            {canEdit && (
              <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
                <Box
                  sx={{
                    backgroundColor: "#F0F1F5",
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    p: 1.5,
                  }}
                >
                  <Typography variant="body2">
                    <strong>Audit Info</strong>
                  </Typography>
                </Box>
                <Box p={1.5}>
                  <Box display="flex" justifyContent="space-between">
                    <Stack>
                      <Typography color="secondary">Created At</Typography>
                      <Typography variant="body2">
                        {formatDateTime(borrowerInfo?.created_at) || "—"}
                      </Typography>
                    </Stack>
                    <Stack alignItems="end">
                      <Typography color="secondary">Modified By</Typography>
                      <Typography variant="body2">
                        {borrowerInfo?.modified_by || "—"}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box py={1}>
                    <Divider />
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Stack></Stack>
                    <Stack alignItems="end">
                      <Typography color="secondary">Modified At</Typography>
                      <Typography variant="body2">
                        {formatDateTime(borrowerInfo?.modified_at) || "—"}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            )}

            {/* <Box sx={{ border: "1px solid #d6d9de", borderRadius: 1.5 }}>
              <Box
                sx={{
                  backgroundColor: "#F0F1F5",
                  borderTopLeftRadius: 5,
                  borderTopRightRadius: 5,
                  p: 1.5,
                  z: 1,
                  position: "relative",
                }}
              >
                <Typography fontWeight="bold" variant="body2">
                  Owner Details
                </Typography>
              </Box>
              <Grid container>
                <Grid item xs={6} md={5}>
                  <Box p={1.5}>
                    <Typography color="secondary">Name:</Typography>
                    <Typography
                      mt={1}
                      variant="body2"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {providerInfo?.owner_name || "--"}
                    </Typography>

                    <Typography mt={3} color="secondary">
                      Email
                    </Typography>
                    <Typography variant="body2">
                      {providerInfo?.email_id || "--"}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box> */}
          </Stack>
        </Grid>
      </Grid>

      <>
        {showUploadDocumentModal && (
          <UploadKycDocumentModal
            open={showUploadDocumentModal}
            onClose={() => setShowUploadDocumentModal(false)}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
          />
        )}

        {showOwnerModal && (
          <EditOwnerModal
            showOwnerModal={showOwnerModal}
            setShowOwnerModal={setShowOwnerModal}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
            type={ownerModalType}
            selectedOwner={selectedOwner}
            setSelectedOwner={setSelectedOwner}
          />
        )}

        {showEditBusinessDetailsModal && (
          <EditBusinessDetailsModal
            showEditBusinessDetailsModal={showEditBusinessDetailsModal}
            setShowEditBusinessDetailsModal={setShowEditBusinessDetailsModal}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
          />
        )}

        {showUploadAgreementModal && (
          <UploadAgreementModal
            open={showUploadAgreementModal}
            onClose={() => setShowUploadAgreementModal(false)}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
          />
        )}

        {showBankDetailsModal && (
          <EditBankDetailsModal
            showBankDetailsModal={showBankDetailsModal}
            setShowBankDetailsModal={setShowBankDetailsModal}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
            type={bankModalType}
            selectedBank={selectedBank}
            setSelectedBank={setSelectedBank}
          />
        )}
        {showEditContactDetailsModal && (
          <EditContactDetailsModal
            showEditContactDetailsModal={showEditContactDetailsModal}
            setShowEditContactDetailsModal={setShowEditContactDetailsModal}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
          />
        )}
        {showEditGstModal && (
          <EditGstModal
            showEditGstModal={showEditGstModal}
            setShowEditGstModal={setShowEditGstModal}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
          />
        )}
        {showEditBusinessPanModal && (
          <EditBusinessPanModal
            showEditBusinessPanModal={showEditBusinessPanModal}
            setShowEditBusinessPanModal={setShowEditBusinessPanModal}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
          />
        )}
        {showEditCreditDetailsModal && (
          <EditCreditDetailsModal
            showEditCreditDetailsModal={showEditCreditDetailsModal}
            setShowEditCreditDetailsModal={setShowEditCreditDetailsModal}
            borrowerInfo={borrowerInfo}
            setBorrowerInfo={setBorrowerInfo}
          />
        )}
      </>
    </div>
  );
};

export default Summary;
